import { FC, useEffect, useMemo, useState } from "react";
import { faAdd, faKey, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { SshKey } from "@hostingbv/lib/src/api/partner/models/SshKey";
import { useNavigate } from "react-router-dom";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { AddSshKeyModal } from "../modals/AddSshKeyModal";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { usePermissions } from "../../../lib/src/auth/permissions";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";

export const SshKeys: FC = () => {
  const { partnerClient } = useApiClient(config);
  const { resource, reload } = useFetchedResource(() => partnerClient.getAllSshKeys());
  const { can } = usePermissions(partnerClient);
  useEffect(() => {
    reload();
  }, []);
  const sshKeys: SshKey[] = useMemo(() => {
    if (resource?.success === true && resource.data) {
      return resource.data;
    }
    return [];
  }, [resource]);
  return (
    <ContentContainer>
      <PageIconHeader icon={faKey}>
        <Breadcrumbs crumbs={[]} currentPage={"SSH Keys"} />
        <PageHeader>SSH Keys</PageHeader>
      </PageIconHeader>
      {can("create", "authorizedKeys") && <AddSshKeyForm />}
      <SshKeyOverview sshKeys={sshKeys} reloadSshKeys={reload} />
    </ContentContainer>
  );
};

const AddSshKeyForm: FC = () => {
  const { open: openCreateModal } = useModal({
    title: "Add new SSH key",
    body: <AddSshKeyModal defaultKeyType={"Personal key"} />,
  });
  return (
    <Callout>
      <SectionHeader>Add new SSH key</SectionHeader>
      <Paragraph>
        SSH keys can be used to manage access to sites and servers. If you decommission a key here, all sites and
        servers that that key has will be updated accordingly.
      </Paragraph>
      <Paragraph>
        We make the distinction between <strong>personal keys</strong> and <strong>deployment keys</strong>. Personal
        keys are linked to a contact in your organization. Deployment keys are meant to be used by automated systems,
        such as your CI/CD pipeline. We recommend you do not use the same deployment key for all your sites and servers,
        but instead create a new key for each site or server.
      </Paragraph>
      <Button size={"sm"} type={"primary"} text={"Add SSH Key"} icon={faAdd} onClick={() => openCreateModal()} />
    </Callout>
  );
};

const SshKeyOverview: FC<{ sshKeys: SshKey[]; reloadSshKeys: () => void }> = (props) => {
  const [keyFilter, setKeyFilter] = useState("All");

  const navigate = useNavigate();
  const sshKeys: SshKey[] = useMemo(() => {
    return props.sshKeys.filter((key) => {
      if (keyFilter === "Personal Keys") {
        return key.type === "personal";
      }
      if (keyFilter === "Deployment Keys") {
        return key.type === "deployment";
      }
      if (keyFilter === "Unused Keys") {
        return key.uses === 0;
      }
      return true;
    });
  }, [props.sshKeys, keyFilter]);
  return (
    <div className={"my-8"}>
      <SectionHeader>Your SSH Keys</SectionHeader>
      <TabLayout
        tabNames={["All", "Personal Keys", "Deployment Keys", "Unused Keys"]}
        activeTab={keyFilter}
        setActiveTab={setKeyFilter}
      />
      <FilterableDataTable
        keyProperty={"id"}
        data={sshKeys}
        searchableProperties={["displayName"]}
        actions={<IconButton icon={faRefresh} onClick={props.reloadSshKeys} type={"secondary"} size={"sm"} />}
        columns={[
          {
            header: "Name",
            property: "displayName",
            sort: true,
          },
          {
            header: "Type",
            property: "type",
            transform: (value) => (value === "personal" ? "Personal" : "Deployment"),
            sort: true,
          },
          {
            header: "Uses",
            property: "uses",
          },
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/ssh-keys/${row.id}`, e)}
      />
    </div>
  );
};
